<template>
  <div class="xinchuang-page">
    <div class="hero">
      <img ref="heroImageRef" src="@/assets/solutions/xinchuang/hero-banner.svg" alt="信创专题背景" class="hero-img" @load="adjustHeroTitleContrast" />
      <div ref="heroTitleRef" class="hero-title" :style="heroTitleStyle">全面拥抱信创解决方案</div>
    </div>

    <section class="content-section ppt-content-section">
      <div class="content-card ppt-content-card">
        <h2 class="section-title">信创建设背景</h2>
        <p>
          国际局势复杂，传统存储面临“卡脖子”风险与数据安全隐患频发。外部技术封锁频发，导致关键存储部件依赖进口，制约自主发展；供应链面临断供风险，安全可控性受到严重挑战；勒索攻击日益频繁，对数据安全构成直接威胁。
        </p>
        <ul class="ppt-list">
          <li>传统存储系统防御机制薄弱，难以应对新型网络攻击。</li>
          <li>安全漏洞频发，敏感数据暴露风险持续上升。</li>
          <li>闭源架构存在隐蔽后门，审计与监控难度大，后门追踪困难。</li>
          <li>构建全栈国产存储底座，是实现自主可控的必然选择。</li>
        </ul>
      </div>

      <div class="content-card ppt-content-card">
        <h2 class="section-title">国家‘信创’战略深化推进，关键行业亟需全栈国产化替代方案</h2>
        <img :src="xinchuangTrendImage" alt="国家信创战略深化推进" class="ppt-banner-image" />
      </div>

      <div class="content-card ppt-content-card">
        <h2 class="section-title">瑞和云图全系列产品，致力于打造100%自主可控的云安全基础设施底座：应对卡脖子风险，守护数据安全，响应国家战略，筑牢关键信息基础设施安全防线</h2>
        <img :src="xinchuangPositionImage" alt="产品定位与价值" class="ppt-banner-image" />
      </div>

      <div class="content-card ppt-content-card">
        <h2 class="section-title">信创全栈适配能力</h2>
        <ul class="ppt-list">
          <li><strong>国产CPU兼容：</strong>支持飞腾、鲲鹏、龙芯、海光等主流国产芯片架构，针对指令集优化内核调度，提升运算效率。</li>
          <li><strong>国产操作系统适配：</strong>全面适配欧拉、麒麟等国产系统生态，支持系统级无缝迁移，保障运行稳定性。</li>
          <li><strong>硬件性能优化：</strong>通过底层调度优化充分发挥国产CPU性能，减少资源开销，提升并发处理能力。</li>
          <li><strong>国产软件支持：</strong>兼容主流国产数据库与中间件，实现应用平滑对接。</li>
          <li><strong>安全生态构建：</strong>从硬件到软件构建可信执行环境，强化系统防护机制，抵御外部安全威胁。</li>
          <li><strong>全栈协同优化：</strong>实现软硬件协同调优，优化启动速度与服务响应时间，增强用户体验。</li>
        </ul>
      </div>
    </section>

    <section class="content-section cert-section">
      <h2 class="section-title">资质证书</h2>
      <div class="cert-grid">
        <img v-for="(cert, idx) in certs" :key="idx" :src="cert" :alt="`信创证书${idx + 1}`" class="cert-img" />
      </div>
    </section>

    
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import cert1 from '@/assets/solutions/xinchuang/cert-1.png'
import cert2 from '@/assets/solutions/xinchuang/cert-2.png'
import cert3 from '@/assets/solutions/xinchuang/cert-3.png'
import cert4 from '@/assets/solutions/xinchuang/cert-4.png'
import cert5 from '@/assets/solutions/xinchuang/cert-5.png'
import xinchuangTrendImage from '@/assets/solutions/xinchuang/ppt-section-1.png'
import xinchuangPositionImage from '@/assets/solutions/xinchuang/ppt-section-2.png'

const certs = [cert1, cert2, cert3, cert4, cert5]
const heroImageRef = ref(null)
const heroTitleRef = ref(null)
const heroTitleStyle = ref({
  color: '#ffffff',
  textShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
})

const srgbToLinear = (v) => {
  const c = v / 255
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

const luminance = (r, g, b) => (
  0.2126 * srgbToLinear(r) +
  0.7152 * srgbToLinear(g) +
  0.0722 * srgbToLinear(b)
)

const contrastRatio = (l1, l2) => {
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

const adjustHeroTitleContrast = async () => {
  await nextTick()
  const img = heroImageRef.value
  const title = heroTitleRef.value
  if (!img || !title) return

  const rect = title.getBoundingClientRect()
  const imgRect = img.getBoundingClientRect()
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx || img.naturalWidth === 0 || img.naturalHeight === 0) return

  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

  const scaleX = canvas.width / imgRect.width
  const scaleY = canvas.height / imgRect.height
  const sx = Math.max(0, Math.floor((rect.left - imgRect.left) * scaleX))
  const sy = Math.max(0, Math.floor((rect.top - imgRect.top) * scaleY))
  const sw = Math.min(canvas.width - sx, Math.max(8, Math.floor(rect.width * scaleX)))
  const sh = Math.min(canvas.height - sy, Math.max(8, Math.floor(rect.height * scaleY)))

  const imageData = ctx.getImageData(sx, sy, sw, sh).data
  let r = 0
  let g = 0
  let b = 0
  const total = imageData.length / 4
  for (let i = 0; i < imageData.length; i += 4) {
    r += imageData[i]
    g += imageData[i + 1]
    b += imageData[i + 2]
  }
  r /= total
  g /= total
  b /= total

  const bgLum = luminance(r, g, b)
  const whiteLum = luminance(255, 255, 255)
  const darkLum = luminance(15, 47, 109)
  const whiteContrast = contrastRatio(whiteLum, bgLum)
  const darkContrast = contrastRatio(darkLum, bgLum)

  if (whiteContrast < 4.5 && darkContrast > whiteContrast) {
    heroTitleStyle.value = {
      color: '#0f2f6d',
      textShadow: '0 2px 10px rgba(255, 255, 255, 0.45)',
    }
    return
  }

  heroTitleStyle.value = {
    color: '#ffffff',
    textShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
  }
}

onMounted(() => {
  adjustHeroTitleContrast()
  window.addEventListener('resize', adjustHeroTitleContrast, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', adjustHeroTitleContrast)
})

defineOptions({
  banner: new URL('@/assets/solutions/xinchuang/hero-banner.svg', import.meta.url).href,
})
</script>

<style scoped>
.xinchuang-page {
  background: #f5f7fb;
}

.hero {
  position: relative;
  overflow: hidden;
  min-height: 300px;
  container-type: inline-size;
}

.hero-img {
  width: 100%;
  height: clamp(260px, 28vw, 360px);
  object-fit: cover;
  display: block;
}

.hero-title {
  position: absolute;
  left: 9%;
  top: 50%;
  transform: translateY(-50%);
  color: #fff;
  font-size: max(32px, 3.229cqi);
  font-weight: 700;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}

.content-section {
  max-width: 1280px;
  margin: 0 auto;
  padding: 42px 24px 12px;
}

.content-card {
  background: #fff;
  border: 1px solid #dbe3f0;
  border-radius: 10px;
  padding: 30px 34px;
  box-shadow: 0 8px 24px -20px rgba(15, 23, 42, 0.25);
}

.content-card p {
  margin: 0;
  font-size: 17px;
  line-height: 1.95;
  color: #374151;
  text-align: justify;
  text-justify: inter-ideograph;
}

.content-card p + p {
  margin-top: 16px;
}

.ppt-content-section {
  padding-top: 10px;
}

.ppt-content-section .content-card + .content-card {
  margin-top: 16px;
}

.ppt-content-card .section-title {
  margin-bottom: 16px;
  text-align: left;
  font-size: 34px;
  line-height: 1.2;
}

.ppt-subtitle {
  margin: 24px 0 10px;
  font-size: 34px;
  line-height: 1.2;
  color: #0f2f6d;
  text-align: left;
}

.ppt-list {
  margin: 12px 0 0;
  padding-left: 22px;
  color: #374151;
  text-align: left;
}

.ppt-list li {
  font-size: 16px;
  line-height: 1.9;
  margin-bottom: 6px;
  text-align: left;
}

.ppt-content-card p {
  text-align: left;
  text-justify: auto;
}

.ppt-banner-image {
  width: 100%;
  display: block;
  border-radius: 8px;
  border: 1px solid #d9e3f2;
  background: #fff;
}

.cert-section {
  padding-top: 26px;
  padding-bottom: 60px;
}

.section-title {
  margin: 0 0 20px;
  font-size: 34px;
  line-height: 1.2;
  color: #0f2f6d;
}

.cert-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.cert-img {
  width: 100%;
  display: block;
  border: 1px solid #d9e3f2;
  border-radius: 8px;
  background: #fff;
}

@media (max-width: 900px) {
  .cert-grid {
    grid-template-columns: 1fr;
  }
}
</style>
